import { useEffect, useLayoutEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { EmojiButton, insertAtCaret, restoreCaret } from '@/components/emoji/EmojiButton';
import { COMPOSE_EVENT, type ComposeDetail } from './reply';
import clsx from 'clsx';
import { SendButton } from '@/components/ui/SendButton';
import { useIsPhone } from '@/mobile/layout';
import { BarChart3, ShieldCheck, Sparkles } from 'lucide-react';
import { AttachButton, UploadChips, useComposerUploads } from '@/components/uploads/ComposerUploads';
import { parseAskCommand, parsePollCommand, sendTimeLabel, type Message } from '@tradeoutlet/shared';
import { ScheduleButton } from './scheduled/ScheduleButton';
import { ScheduledLine } from './scheduled/ScheduledLine';
import { refreshScheduled } from './scheduled/scheduledQueries';
import { api, errorMessage } from '@/lib/api';
import { useMembers, useWorkspaceId } from '@/lib/hooks';
import { messageStore } from '@/lib/messageStore';
import { useUi } from '@/lib/store';
import { Avatar } from '@/components/ui';
import { encodeMentions, mentionCandidates } from '@/lib/mentions';
import { encodeEntities, rememberMention } from '@/lib/entityMentions';
import { MENTION_KIND_LABEL, MENTION_KINDS, type MentionKind } from '@tradeoutlet/shared';
import { useQuery } from '@tanstack/react-query';
import { useDebounced } from '@/lib/useDebounced';
import { MENTION_ICON } from './EntityChip';
import { useAuth } from '@/lib/auth';
import { isSendEnter } from '@/lib/enterToSend';

/** t121: one menu for @ and #: People plus things (buildings, tasks, projects, journeys, issues, channels), in tabs. */
type MentionTab = 'people' | 'all' | MentionKind;
interface MentionRow { key: string; kind: 'person' | MentionKind; id: string; label: string; sub?: string | null }


export { decodeMentions, encodeMentions } from '@/lib/mentions';

export interface AckSendDraft { body: string; parent_id: string | null; mentions: string[]; attachment_ids: string[]; attachments: Message['attachments'] }

const TABS: MentionTab[] = ['people', 'all', ...MENTION_KINDS];

export function Composer({ conversationId, parentId, placeholder, memberIds, autoFocus, onPoll, onPostWithAck, callId, canSchedule }: {
  conversationId: string; parentId?: string | null; placeholder: string; memberIds?: string[]; autoFocus?: boolean;
  /** t622u: offer "Schedule" next to Send (channels, DMs, group DMs and their threads). */
  canSchedule?: boolean;
  /** In-call chat panel (t133u): messages are normal posts here, marked "sent during call". */
  callId?: string;
  /** Top-level composer only: opens the poll/survey dialog ("/poll Question | A | B" prefills it). */
  onPoll?: (draft: { question: string; options: string[] }) => void;
  /** Backlog #16: post this message and require acknowledgement (opens the recipients dialog; `clear` empties the composer once posted). */
  onPostWithAck?: (draft: AckSendDraft, clear: () => void) => void;
}) {
  const phone = useIsPhone();
  const [ackMode, setAckMode] = useState(false);
  const ws = useWorkspaceId();
  const { members } = useMembers();
  const { userId } = useAuth();
  const toast = useUi(s => s.toast);
  const openAssistant = useUi(s => s.openAssistant);
  const draftKey = `collab.draft.${conversationId}.${parentId ?? 'root'}`;
  const [text, setText] = useState(() => sessionStorage.getItem(draftKey) ?? '');
  const [picked] = useState(() => new Map<string, string>());
  const [mention, setMention] = useState<{ query: string; start: number; trigger: '@' | '#' } | null>(null);
  const [tab, setTab] = useState<MentionTab>('people');
  const [pickedEnt] = useState(() => new Map<string, { kind: MentionKind; id: string }>());
  const [active, setActive] = useState(0);
  // t509u: shared with "Message people on shifts" (components/uploads).
  const up = useComposerUploads(() => (ws ? { workspace_id: ws, conversation_id: conversationId } : null));
  const { uploads, addFiles } = up;
  const ref = useRef<HTMLTextAreaElement>(null);
  const lastTyping = useRef(0);
  const caretAfter = useRef<number | null>(null);
  useLayoutEffect(() => {
    const pos = caretAfter.current;
    if (pos === null || !ref.current) return;
    caretAfter.current = null;
    ref.current.focus();
    ref.current.setSelectionRange(pos, pos);
  }, [text]);

  useEffect(() => { sessionStorage.setItem(draftKey, text); }, [draftKey, text]);
  useEffect(() => { if (autoFocus) ref.current?.focus(); }, [autoFocus, conversationId]);
  // Reply / quote-reply from a message (double-click, R, swipe, menu): focus this composer and add the quote.
  useEffect(() => {
    const on = (e: Event) => {
      const d = (e as CustomEvent<ComposeDetail>).detail;
      if (d.conversationId !== conversationId || d.parentId !== (parentId ?? null)) return;
      if (d.quote) setText(t => `${d.quote}\n${t.startsWith(d.quote!) ? t.slice(d.quote!.length + 1) : t}`);
      requestAnimationFrame(() => { const el = ref.current; if (!el) return; el.focus(); el.setSelectionRange(el.value.length, el.value.length); });
    };
    window.addEventListener(COMPOSE_EVENT, on);
    return () => window.removeEventListener(COMPOSE_EVENT, on);
  }, [conversationId, parentId]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 240)}px`;
  }, [text]);

  const people = useMemo(() => {
    if (!mention || tab !== 'people') return [];
    const pool = memberIds ? members.filter(m => memberIds.includes(m.user_id)) : members;
    const q = mention.query.toLowerCase();
    // Never suggest yourself (chat, DMs, channels and every entity thread use this composer).
    return mentionCandidates(pool, q, userId);
  }, [mention, tab, members, memberIds, userId]);
  const entQuery = useDebounced(mention && tab !== 'people' ? mention.query : '', 150);
  const things = useQuery({
    queryKey: ['mention-search', ws, tab, entQuery], enabled: Boolean(ws && mention && tab !== 'people'), staleTime: 30_000,
    queryFn: () => api.mentionSearch(ws!, entQuery, tab === 'all' || tab === 'people' ? undefined : tab).then(r => r.items),
  });
  const candidates: MentionRow[] = !mention ? [] : tab === 'people'
    ? people.map(c => ({ key: c.user_id, kind: 'person' as const, id: c.user_id, label: c.display_name, sub: c.job_title }))
    : (things.data ?? []).map(i => ({ key: `${i.kind}:${i.id}`, kind: i.kind, id: i.id, label: i.label, sub: i.sub }));

  const onChange = (value: string, caret: number) => {
    setText(value);
    const before = value.slice(0, caret);
    const m = /(^|\s)([@#])([\w'-]*(?: [\w'-]*)?)$/.exec(before);
    const trigger = m?.[2] as '@' | '#' | undefined;
    if (trigger && (!mention || mention.trigger !== trigger)) setTab(trigger === '@' ? 'people' : 'all');
    setMention(m && trigger ? { query: m[3], start: caret - m[3].length - 1, trigger } : null);
    setActive(0);
    const now = Date.now();
    if (value && now - lastTyping.current > 3000) {
      lastTyping.current = now;
      void api.typing(conversationId, parentId).catch(() => undefined);
    }
  };

  const pickMention = (row: MentionRow) => {
    if (!mention) return;
    const caret = ref.current?.selectionStart ?? text.length;
    const name = row.kind === 'person' ? row.label : row.label.replace(/^#/, '');
    const sigil = row.kind === 'person' ? '@' : '#';
    const next = `${text.slice(0, mention.start)}${sigil}${name} ${text.slice(caret)}`;
    if (row.kind === 'person') picked.set(name, row.id);
    else { pickedEnt.set(name, { kind: row.kind, id: row.id }); if (ws) rememberMention(ws, { kind: row.kind, id: row.id, label: row.label, sub: row.sub }); }
    // Place the caret in the same commit as the new text, so a key pressed straight after picking lands after the mention.
    caretAfter.current = mention.start + name.length + 2;
    setText(next);
    setMention(null);
  };

  const { busy, ready } = up;

  const ask = parseAskCommand(text);
  const pollCmd = onPoll ? parsePollCommand(text) : null;
  const send = () => {
    const trimmed = text.trim();
    // "/ask <question>" goes to the private assistant panel, never into the conversation.
    if (ask !== null) {
      if (!ask) { toast('Type a question after /ask', 'info'); return; }
      openAssistant(ask, 'slash');
      setText('');
      sessionStorage.removeItem(draftKey);
      return;
    }
    if (pollCmd && onPoll) {
      onPoll(pollCmd);
      setText('');
      sessionStorage.removeItem(draftKey);
      return;
    }
    if ((!trimmed && ready.length === 0) || busy) return;
    const { body, mentions } = encodeMentions(encodeEntities(trimmed, pickedEnt), picked);
    if (ackMode && onPostWithAck) {
      if (!body) return;
      onPostWithAck({ body, parent_id: parentId ?? null, mentions, attachment_ids: ready.map(a => a.id), attachments: ready }, () => {
        setText(''); up.clear(); picked.clear(); pickedEnt.clear(); sessionStorage.removeItem(draftKey); setAckMode(false);
      });
      return;
    }
    void messageStore.send(conversationId, { body, parent_id: parentId ?? null, mentions, attachment_ids: ready.map(a => a.id), attachments: ready, call_id: callId ?? null });
    setText('');
    up.clear();
    picked.clear();
    pickedEnt.clear();
    sessionStorage.removeItem(draftKey);
  };

  // t622u: write now, send later. Same body/mentions/files as Send; the composer clears once the server has it.
  const scheduling = Boolean(canSchedule && !callId && !ackMode && ask === null && !pollCmd);
  const [scheduleBusy, setScheduleBusy] = useState(false);
  const schedule = async (sendAt: string) => {
    const trimmed = text.trim();
    if ((!trimmed && ready.length === 0) || busy || scheduleBusy) return;
    const { body, mentions } = encodeMentions(encodeEntities(trimmed, pickedEnt), picked);
    setScheduleBusy(true);
    try {
      const r = await api.scheduleMessage(conversationId, { body, parent_id: parentId ?? null, mentions, attachment_ids: ready.map(a => a.id), send_at: sendAt });
      setText(''); up.clear(); picked.clear(); pickedEnt.clear(); sessionStorage.removeItem(draftKey);
      void refreshScheduled();
      toast(`Scheduled for ${sendTimeLabel(r.send_at)}`, 'success');
      ref.current?.focus();
    } catch (e) { toast(errorMessage(e), 'error'); }
    finally { setScheduleBusy(false); }
  };

  const onKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (mention && e.altKey && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) {
      e.preventDefault(); const i = TABS.indexOf(tab); setTab(TABS[(i + (e.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length]); setActive(0); return;
    }
    if (mention && e.key === 'Escape') { e.preventDefault(); setMention(null); return; }
    if (mention && candidates.length) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => (a + 1) % candidates.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => (a - 1 + candidates.length) % candidates.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickMention(candidates[active] ?? candidates[0]); return; }
      if (e.key === 'Escape') { setMention(null); return; }
    }
    if (e.key === 'ArrowUp' && !text && !e.shiftKey && !e.altKey && !e.ctrlKey && !e.metaKey) {
      const rows = document.querySelectorAll<HTMLElement>(`[data-message-list="${parentId ? 'thread' : 'main'}"] [data-message-row]`);
      if (rows.length) { e.preventDefault(); rows[rows.length - 1].focus(); rows[rows.length - 1].scrollIntoView({ block: 'nearest' }); return; }
    }
    // t264u: the shared rule (no send mid-IME; empty text isn't sent - send() also checks, for attachments).
    if (isSendEnter(e)) { e.preventDefault(); send(); }
  };

  const onPaste = (e: ClipboardEvent) => { if (e.clipboardData.files.length) { e.preventDefault(); addFiles(e.clipboardData.files); } };

  return (
    <div className="relative px-4 pb-4"
      onDragOver={e => e.preventDefault()}
      onDrop={e => { e.preventDefault(); if (e.dataTransfer.files.length) addFiles(e.dataTransfer.files); }}>
      {onPoll && (pollCmd !== null || (text.startsWith('/') && text.trim().length > 1 && '/poll'.startsWith(text.trim().toLowerCase()))) && (
        <div className="absolute bottom-full left-4 z-20 mb-1 flex w-96 items-start gap-2 rounded-lg bg-white px-3 py-2 text-sm shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <BarChart3 className="mt-0.5 h-4 w-4 shrink-0 text-accent-fg" />
          <div><span className="font-semibold">/poll</span> <span className="text-slate-500">Question | Option 1 | Option 2</span>
            <div className="text-xs text-slate-500">Press Enter to review and post the poll.</div></div>
        </div>
      )}
      {(ask !== null || (text.startsWith('/') && '/ask'.startsWith(text.trim().toLowerCase()))) && (
        <div className="absolute bottom-full left-4 z-20 mb-1 flex w-96 items-start gap-2 rounded-lg bg-white px-3 py-2 text-sm shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-accent-fg" />
          <div><span className="font-semibold">/ask</span> <span className="text-slate-500">your question</span>
            <div className="text-xs text-slate-500">Asks the assistant privately - nothing is posted to this conversation.</div></div>
        </div>
      )}
      {mention && (tab !== 'people' || candidates.length > 0) && (
        <div className="absolute bottom-full left-4 z-20 mb-1 w-[26rem] max-w-[calc(100%-2rem)] overflow-hidden rounded-lg bg-white shadow-xl ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700" data-testid="mention-menu">
          <div className="flex gap-0.5 overflow-x-auto border-b border-slate-200 px-1 pt-1 text-xs dark:border-slate-700" role="tablist" aria-label="Mention">
            {TABS.map(t => (
              <button key={t} type="button" role="tab" aria-selected={tab === t} data-testid={`mention-tab-${t}`} onMouseDown={e => { e.preventDefault(); setTab(t); setActive(0); }}
                className={clsx('shrink-0 rounded-t px-2 py-1 font-medium', tab === t ? 'border-b-2 border-black text-slate-900 dark:border-white dark:text-white' : 'text-slate-500 hover:text-slate-800 dark:hover:text-slate-200')}>
                {t === 'people' ? 'People' : t === 'all' ? 'All' : MENTION_KIND_LABEL[t]}
              </button>
            ))}
          </div>
          <div className="max-h-72 overflow-y-auto py-1">
            {candidates.map((c, i) => {
              const Icon = c.kind === 'person' ? null : MENTION_ICON[c.kind];
              return (
                <button key={c.key} type="button" data-testid="mention-option" onMouseDown={e => { e.preventDefault(); pickMention(c); }}
                  className={clsx('flex w-full items-center gap-2 px-3 py-1.5 text-left text-sm', i === active ? 'bg-accent text-on-accent' : 'hover:bg-slate-100 dark:hover:bg-slate-700')}>
                  {Icon ? <Icon className="h-4 w-4 shrink-0" /> : <Avatar size={22} name={c.label} id={c.id} />}
                  <span className="truncate font-medium">{c.label}</span>
                  {c.sub && <span className={clsx('ml-auto shrink-0 truncate text-xs', i === active ? 'text-on-accent/70' : 'text-slate-500')}>{c.sub}</span>}
                </button>
              );
            })}
            {!candidates.length && <div className="px-3 py-2 text-sm text-slate-500">{things.isFetching ? 'Searching…' : 'Nothing found'}</div>}
          </div>
        </div>
      )}
      {/* Focus: the soft border change the composer always had (darker grey border + faint grey glow), never the black/yellow ring. slate-500 on white is 4.8:1. */}
      {canSchedule && !callId && <ScheduledLine conversationId={conversationId} parentId={parentId} />}
      <div className="rounded-xl border border-slate-300 bg-white shadow-sm focus-within:border-slate-500 focus-within:ring-2 focus-within:ring-slate-400/25 dark:border-slate-700 dark:bg-slate-900 dark:focus-within:border-slate-400 dark:focus-within:ring-slate-500/30" data-testid="composer-box">
        <UploadChips uploads={uploads} onRemove={up.remove} className="border-b border-slate-100 p-2 dark:border-slate-800" />
        <textarea ref={ref} rows={1} value={text} placeholder={placeholder}
          onChange={e => onChange(e.target.value, e.target.selectionStart)}
          onKeyDown={onKeyDown} onPaste={onPaste}
          className={clsx('block max-h-60 w-full resize-none bg-transparent px-3.5 pt-2.5 outline-none placeholder:text-slate-400', phone ? 'text-base' : 'text-[14.5px]')} />
        <div className="flex items-center justify-between px-2 pb-1.5 pt-0.5">
          <div className="flex min-w-0 items-center gap-1">
            <EmojiButton onPick={e => { const r = insertAtCaret(ref.current, text, e); onChange(r.value, r.caret); restoreCaret(ref.current, r.caret); }} />
            <AttachButton onFiles={addFiles} />
            {onPostWithAck && <button title={ackMode ? 'Requires acknowledgement (click to turn off)' : 'Require acknowledgement when posting'} aria-label="Require acknowledgement" aria-pressed={ackMode} onClick={() => setAckMode(!ackMode)} className={clsx('rounded p-1.5', ackMode ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-200' : 'text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800')}><ShieldCheck className="h-4 w-4" /></button>}
            {onPoll && <button title="Poll or survey" aria-label="Create a poll or survey" onClick={() => onPoll({ question: '', options: [] })} className="rounded p-1.5 text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-800"><BarChart3 className="h-4 w-4" /></button>}
          </div>
          <div className="flex shrink-0 items-center gap-0.5">
            {scheduling && <ScheduleButton phone={phone} onSchedule={at => void schedule(at)} disabled={busy || scheduleBusy || (!text.trim() && ready.length === 0)} />}
            <SendButton phone={phone} onClick={send} disabled={busy || scheduleBusy || (!text.trim() && ready.length === 0)} data-testid="composer-send" />
          </div>
        </div>
      </div>
    </div>
  );
}
